<template>
  <div class="rendering-layer-form">
    <a-alert
      v-if="noStoredRow && inheritMessage"
      type="info"
      show-icon
      class="mb-3"
      :message="inheritMessage"
    />

    <a-row :gutter="24">
      <a-col v-for="panel in panels" :key="panel.key" :span="12">
        <div class="config-section" :class="{ 'config-section--mirror': panel.readOnly }">
          <div class="config-section__title">{{ panel.title }}</div>
          <div v-if="isStaff" class="config-section__hint">
            Rendering engine settings for this layer.
          </div>

          <div v-if="isStaff" class="config-field config-field--engine">
            <div class="config-field__label">
              {{ panel.engineLabel }}
              <a-tag color="orange" class="config-field__tag">staff</a-tag>
            </div>
            <a-select
              v-model="engineModels[panel.engineField]"
              allow-clear
              show-search
              option-filter-prop="children"
              style="width: 100%"
              @change="onEngineSelected"
            >
              <a-select-option
                v-for="engine in enginesFor(panel.engineKind)"
                :key="engine.engine_code"
                :value="engine.engine_code"
              >
                {{ engine.engine_label || engine.engine_code }} ({{ engine.engine_code }})
              </a-select-option>
            </a-select>
          </div>

          <a-empty
            v-if="panel.fields.length === 0"
            :image="simpleEmptyImage"
            :description="
              panel.keysKnown
                ? 'This engine has no layer settings.'
                : 'The engine layer keys are not available.'
            "
          />

          <div class="config-grid config-grid--2">
            <RenderingConfigField
              v-for="field in panel.publicFields"
              :key="field.key"
              :field="field"
              :value="panel.model[field.key]"
              :disabled="panel.readOnly"
              :user-agents="userAgents"
              @input="onFieldInput(panel.key, field.key, $event)"
            />
            <div v-if="panel.staffFields.length" class="config-fields-divider">
              <a-tag color="orange" class="config-field__tag">staff</a-tag>
            </div>
            <RenderingConfigField
              v-for="field in panel.staffFields"
              :key="field.key"
              :field="field"
              :value="panel.model[field.key]"
              :disabled="panel.readOnly"
              :user-agents="userAgents"
              @input="onFieldInput(panel.key, field.key, $event)"
            />
          </div>
        </div>
      </a-col>
    </a-row>

    <div v-if="isStaff" class="config-section mt-3">
      <div
        class="config-section__toggle"
        :class="{ 'config-section__toggle--open': curlOpen }"
        role="button"
        tabindex="0"
        @click="curlOpen = !curlOpen"
        @keydown.enter="curlOpen = !curlOpen"
        @keydown.space.prevent="curlOpen = !curlOpen"
      >
        <a-icon type="caret-right" :rotate="curlOpen ? 90 : 0" />
        <span class="config-section__title config-section__title--flush">
          Curl config
        </span>
        <a-tag color="orange" class="config-field__tag">staff</a-tag>
        <a-tag v-if="curlOverrideCount" color="blue" class="config-field__tag">
          {{ curlOverrideCount }} set
        </a-tag>
      </div>

      <div v-show="curlOpen">
        <div class="config-section__hint">
          Every field shows the value in effect. Clear a field to inherit it again;
          only a value you change is written to this layer.
        </div>

        <div class="config-grid config-grid--3">
          <RenderingConfigField
            v-for="field in curlFieldList"
            :key="field.key"
            :field="field"
            :value="forms.curl_config[field.key]"
            @input="onCurlFieldInput(field.key, $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { Empty } from 'ant-design-vue';
  import {
    CONFIG_SECTIONS,
    fieldsFor,
    sharedFieldsFor,
    curlFields,
    configsAcceptingKey,
    configToForm,
    formToConfig,
    emptyConfigPair,
  } from '../consts/RenderingConfig.consts';
  import RenderingConfigField from './RenderingConfigField.vue';
  import '../styles/rendering-config.css';

  export default {
    name: 'RenderingLayerForm',
    components: { RenderingConfigField },
    props: {
      isStaff: { type: Boolean, default: false },
      noStoredRow: { type: Boolean, default: false },
      /** Where values come from when this layer has no stored override. */
      inheritMessage: { type: String, default: '' },
      /** One engine renders both outputs, so a single config panel drives both blobs. */
      shared: { type: Boolean, default: false },
      engines: { type: Array, default: () => [] },
      /** Rows from tblUserAgent, used by the User agent select. */
      userAgents: { type: Array, default: () => [] },
      /** Values stored on this layer row. */
      stored: { type: Object, default: () => ({}) },
      /** Effective values from parent layers and engine defaults. */
      inherited: { type: Object, default: emptyConfigPair },
      /**
       * Layer keys the effective engine reads, per config blob, as reported by the API.
       * Null means unknown, and then no field is offered.
       */
      supportedKeys: { type: Object, default: null },
      /**
       * key_policy.customer_keys from the API. Keys on this list are public; any other
       * offered layer key is staff-only. Null means the policy is not loaded yet.
       */
      customerKeys: { type: Object, default: null },
      /** Engine codes in effect above this layer, so the select is never blank. */
      inheritedEngines: { type: Object, default: () => ({}) },
    },
    data() {
      return {
        simpleEmptyImage: Empty.PRESENTED_IMAGE_SIMPLE,
        engineModels: { html_engine: null, screenshot_engine: null },
        /** The stored + inherited codes the selects were last filled from. */
        engineBaseKey: null,
        ownEngines: [],
        forms: emptyConfigPair(),
        ownKeys: { html_config: [], screenshot_config: [], curl_config: [] },
        sharedForm: {},
        sharedOwnKeys: [],
        /**
         * Keys the user changed since the last load of this layer. A save on another tab
         * changes the inherited values here, and a rebuild must keep these edits.
         */
        dirtyKeys: { html_config: [], screenshot_config: [], curl_config: [] },
        /** The Curl config box starts collapsed; it holds every curl key. */
        curlOpen: false,
        /** The user picked an engine that is not saved yet. */
        engineDirty: false,
        /** "Reset to defaults" dropped the ownership of every key until the next save. */
        ownershipCleared: false,
      };
    },
    computed: {
      sharedFields() {
        const supported = this.supportedKeys ? this.supportedKeys.screenshot_config : null;
        return sharedFieldsFor(supported);
      },
      curlFieldList() {
        return curlFields();
      },
      /** How many curl keys this layer would write, shown on the collapsed header. */
      curlOverrideCount() {
        return Object.keys(this.curlOverrides()).length;
      },
      /**
       * Two boxes, one per output. In shared mode both boxes show the same field set and
       * the screenshot box is read-only, because one engine renders both outputs.
       */
      panels() {
        return CONFIG_SECTIONS.map((section, index) => {
          const isScreenshot = section.name === 'screenshot_config';
          const fields = this.shared ? this.sharedFields : this.fieldsOf(section.name);
          // Shared mode shows the screenshot field set; use that allowlist for the split.
          const policySection = this.shared ? 'screenshot_config' : section.name;
          const publicFields = [];
          const staffFields = [];
          fields.forEach((field) => {
            if (this.isCustomerKey(policySection, field.key)) {
              publicFields.push(field);
            } else {
              staffFields.push(field);
            }
          });
          return {
            key: section.name,
            title: section.title,
            engineField: isScreenshot ? 'screenshot_engine' : 'html_engine',
            engineLabel: isScreenshot ? 'Screenshot engine' : 'HTML engine',
            engineKind: isScreenshot ? 'screenshot' : 'html',
            readOnly: this.shared && index > 0,
            fields,
            publicFields,
            staffFields,
            model: this.shared ? this.sharedForm : this.forms[section.name],
            keysKnown: Array.isArray(
              this.shared
                ? this.supportedKeysOf('screenshot_config')
                : this.supportedKeysOf(section.name)
            ),
          };
        });
      },
    },
    watch: {
      stored: {
        immediate: true,
        deep: true,
        handler() {
          this.rebuild();
        },
      },
      inherited: {
        deep: true,
        handler() {
          this.rebuild();
        },
      },
      inheritedEngines: {
        deep: true,
        handler() {
          this.rebuild();
        },
      },
      supportedKeys: {
        deep: true,
        handler() {
          this.rebuild();
        },
      },
      shared() {
        this.rebuild();
      },
    },
    methods: {
      enginesFor(kind) {
        return (this.engines || []).filter(
          (engine) => engine.engine_kind === kind || engine.engine_kind === 'both'
        );
      },
      /**
       * Stored values this save carries back, so a key the form does not render is not
       * dropped. A customer response already omits staff keys; the API keeps those on the row.
       */
      storedKeysToCarry(sectionName) {
        const storedConfig = (this.stored && this.stored[sectionName]) || {};
        return Object.assign({}, storedConfig);
      },
      /** Keys this layer owns. */
      ownKeysFromStored(storedConfig) {
        return Object.keys(storedConfig || {});
      },
      supportedKeysOf(sectionName) {
        return this.supportedKeys ? this.supportedKeys[sectionName] : null;
      },
      /**
       * True when the API lists this key as customer-visible. With no policy yet, treat
       * every key as public so the form does not invent staff tags.
       */
      isCustomerKey(sectionName, key) {
        const list = this.customerKeys && this.customerKeys[sectionName];
        if (!Array.isArray(list)) {
          return true;
        }
        return list.indexOf(key) !== -1;
      },
      fieldsOf(sectionName) {
        return fieldsFor(sectionName, this.shared, this.supportedKeysOf(sectionName));
      },
      isOwnKey(sectionName, key) {
        return this.ownKeys[sectionName].indexOf(key) !== -1;
      },
      isSharedOwnKey(key) {
        return this.sharedOwnKeys.indexOf(key) !== -1;
      },
      /** The worker merges the screenshot blob first, then lets the HTML blob win. */
      flatten(pair) {
        return Object.assign(
          {},
          (pair && pair.screenshot_config) || {},
          (pair && pair.html_config) || {}
        );
      },
      /** Fill the selects from the codes in effect, unless they already hold them. */
      setEngineBase(baseEngines) {
        const key = `${baseEngines.html_engine}|${baseEngines.screenshot_engine}`;
        if (key === this.engineBaseKey) {
          return;
        }
        this.engineBaseKey = key;
        if (this.engineDirty) {
          return;
        }
        this.engineModels = baseEngines;
      },
      /**
       * A shared edit belongs to every config blob that accepts the key, so the split
       * model keeps the same value. Switching the engine mode then keeps the input.
       */
      onFieldInput(sectionName, key, value) {
        const targets = this.shared ? configsAcceptingKey(key) : [sectionName];
        if (this.shared) {
          this.$set(this.sharedForm, key, value);
        }
        targets.forEach((name) => {
          this.$set(this.forms[name], key, value);
          this.markDirty(this.dirtyKeys[name], key);
        });
      },
      onCurlFieldInput(key, value) {
        if (!this.forms.curl_config) {
          this.$set(this.forms, 'curl_config', {});
        }
        this.$set(this.forms.curl_config, key, value);
        this.markDirty(this.dirtyKeys.curl_config, key);
      },
      markDirty(keys, key) {
        if (keys.indexOf(key) === -1) {
          keys.push(key);
        }
      },
      /** True while the form holds input that no save carried to the API yet. */
      hasUnsavedChanges() {
        return Boolean(
          this.engineDirty
          || this.ownershipCleared
          || CONFIG_SECTIONS.some((section) => this.dirtyKeys[section.name].length)
          || (this.dirtyKeys.curl_config && this.dirtyKeys.curl_config.length)
        );
      },
      /** Forget the unsaved edits. The next rebuild then shows the server values again. */
      clearDirty() {
        this.dirtyKeys = { html_config: [], screenshot_config: [], curl_config: [] };
        this.engineDirty = false;
        this.ownershipCleared = false;
      },
      /**
       * A different engine brings a different default for every key, so the parent reloads
       * the inherited values before the fields are rebuilt.
       */
      onEngineSelected() {
        this.engineDirty = true;
        this.$emit('engine-change', {
          html_engine: this.engineModels.html_engine || null,
          screenshot_engine: this.engineModels.screenshot_engine || null,
        });
      },
      /**
       * Rebuild from the props even when the values did not change. The parent calls this
       * after a reload of this layer, where the user asked for the server values back.
       */
      resetFromProps() {
        this.clearDirty();
        this.engineBaseKey = null;
        this.rebuild();
      },
      rebuild() {
        const stored = this.stored || {};
        const inherited = this.inherited || {};
        const inheritedEngines = this.inheritedEngines || {};

        // Like every other field, the select shows the code in effect.
        const baseEngines = {
          html_engine: stored.html_engine || inheritedEngines.html_engine || null,
          screenshot_engine:
            stored.screenshot_engine || inheritedEngines.screenshot_engine || null,
        };
        // Picking an engine rebuilds the fields from that engine's defaults, which runs
        // rebuild() again. Only a change in the stored or inherited codes may move the select,
        // or that re-render would undo the pick.
        this.setEngineBase(baseEngines);
        this.ownEngines = ['html_engine', 'screenshot_engine'].filter((field) => stored[field]);

        // Both models are rebuilt, so a switch between a shared and a split engine shows
        // the values the other mode already held instead of the server values again.
        const forms = {};
        const ownKeys = {};
        CONFIG_SECTIONS.forEach((section) => {
          const fields = this.fieldsOf(section.name);
          const storedConfig = stored[section.name] || {};
          const inheritedConfig = inherited[section.name] || {};
          // Every field shows the value in effect: this layer wins, else the parent value.
          forms[section.name] = this.keepDirtyValues(
            section.name,
            configToForm(Object.assign({}, inheritedConfig, storedConfig), fields)
          );
          ownKeys[section.name] = this.ownershipCleared
            ? []
            : this.ownKeysFromStored(storedConfig);
        });

        if (this.isStaff) {
          const curlStored = stored.curl_config || {};
          const curlInherited = inherited.curl_config || {};
          forms.curl_config = this.keepDirtyValues(
            'curl_config',
            configToForm(Object.assign({}, curlInherited, curlStored), this.curlFieldList)
          );
          ownKeys.curl_config = this.ownershipCleared
            ? []
            : this.ownKeysFromStored(curlStored);
        } else {
          forms.curl_config = {};
          ownKeys.curl_config = [];
        }

        this.syncShared(stored, inherited);
        this.forms = forms;
        this.ownKeys = ownKeys;
      },
      /**
       * A rebuild reads the server values, so a key the user typed into would go back to
       * the stored or inherited value. Put the unsaved input back over the fresh form.
       *
       * A shared edit is recorded on every config blob that accepts the key, so the shared
       * model reads the same store as the split one.
       */
      keepDirtyValues(sectionName, form) {
        // The shared model holds one value per key, so it reads both blobs in the order
        // flatten() uses: the screenshot value first, then the HTML value over it.
        const sources = sectionName
          ? [sectionName]
          : ['screenshot_config', 'html_config'];
        sources.forEach((name) => {
          const previous = this.forms[name] || {};
          (this.dirtyKeys[name] || []).forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(form, key)) {
              form[key] = previous[key];
            }
          });
        });
        return form;
      },
      syncShared(stored, inherited) {
        const storedFlat = this.flatten(stored);
        const inheritedFlat = this.flatten(inherited);
        const nextForm = this.keepDirtyValues(
          null,
          configToForm(Object.assign({}, inheritedFlat, storedFlat), this.sharedFields)
        );
        const ownStoredKeys = this.ownKeysFromStored(storedFlat);
        this.sharedForm = nextForm;
        this.sharedOwnKeys = this.ownershipCleared
          ? []
          : this.sharedFields
            .map((field) => field.key)
            .filter((key) => ownStoredKeys.indexOf(key) !== -1);
      },
      /**
       * Keys hidden from the shared editor stay untouched, so switching a URL to a shared
       * engine does not drop curl overrides that a different engine would still use.
       */
      buildSharedPayload() {
        const out = {
          html_config: this.storedKeysToCarry('html_config'),
          screenshot_config: this.storedKeysToCarry('screenshot_config'),
        };

        const current = formToConfig(this.sharedForm, this.sharedFields);
        const inheritedValues = formToConfig(
          configToForm(this.flatten(this.inherited || {}), this.sharedFields),
          this.sharedFields
        );

        this.sharedFields.forEach((field) => {
          const targets = configsAcceptingKey(field.key);
          targets.forEach((name) => {
            delete out[name][field.key];
          });

          if (!Object.prototype.hasOwnProperty.call(current, field.key)) {
            return;
          }
          const changed =
            JSON.stringify(current[field.key]) !== JSON.stringify(inheritedValues[field.key]);
          if (!changed && !this.isSharedOwnKey(field.key)) {
            return;
          }
          targets.forEach((name) => {
            out[name][field.key] = current[field.key];
          });
        });

        return out;
      },
      /**
       * Only keys this layer already owned, or values changed away from the inherited
       * value, are written. Untouched inherited values stay inherited.
       */
      buildPayload() {
        const payload = this.shared ? this.buildSharedPayload() : {};
        const inherited = this.inherited || {};

        if (!this.shared) {
          CONFIG_SECTIONS.forEach((section) => {
            const fields = this.fieldsOf(section.name);
            const fieldKeys = fields.map((field) => field.key);
            const current = formToConfig(this.forms[section.name], fields);
            const inheritedConfig = formToConfig(
              configToForm(inherited[section.name] || {}, fields),
              fields
            );

            // Keys the form does not show stay on the layer.
            const carried = this.storedKeysToCarry(section.name);
            const out = {};
            Object.keys(carried).forEach((key) => {
              if (fieldKeys.indexOf(key) === -1) {
                out[key] = carried[key];
              }
            });
            Object.keys(current).forEach((key) => {
              const changed =
                JSON.stringify(current[key]) !== JSON.stringify(inheritedConfig[key]);
              if (changed || this.isOwnKey(section.name, key)) {
                out[key] = current[key];
              }
            });
            payload[section.name] = out;
          });
        }

        this.applyCurlPayload(payload);
        this.applyEnginePayload(payload);
        return payload;
      },
      /** Staff curl overrides live in curlConfig. */
      applyCurlPayload(payload) {
        if (!this.isStaff) {
          return;
        }
        payload.curl_config = this.curlOverrides();
      },
      /**
       * The curl keys this layer writes. Every curl field sits on the form and shows the
       * value in effect, so an untouched inherited value must not become an override.
       * Only a value changed away from the parent, or a key this layer already owned,
       * is written. Clearing an owned field drops the key and inherits it again.
       */
      curlOverrides() {
        const fields = this.curlFieldList;
        const current = formToConfig(this.forms.curl_config || {}, fields);
        const inheritedConfig = formToConfig(
          configToForm((this.inherited && this.inherited.curl_config) || {}, fields),
          fields
        );

        const out = {};
        Object.keys(current).forEach((key) => {
          const changed =
            JSON.stringify(current[key]) !== JSON.stringify(inheritedConfig[key]);
          if (changed || this.isOwnKey('curl_config', key)) {
            out[key] = current[key];
          }
        });
        return out;
      },
      /** An engine that still matches the inherited code stays inherited, not pinned. */
      applyEnginePayload(payload) {
        if (!this.isStaff) {
          return;
        }
        const inheritedEngines = this.inheritedEngines || {};
        ['html_engine', 'screenshot_engine'].forEach((field) => {
          const selected = this.engineModels[field] || null;
          const own = this.ownEngines.indexOf(field) !== -1;
          if (!selected) {
            if (own) {
              payload[field] = null;
            }
            return;
          }
          if (selected !== (inheritedEngines[field] || null) || own) {
            payload[field] = selected;
          }
        });
      },
      /**
       * Reset the form to the given values without saving. The layer stops owning any key,
       * so a following save removes the overrides instead of rewriting them.
       */
      applyDefaults(htmlConfig, screenshotConfig, curlConfig) {
        const inheritedEngines = this.inheritedEngines || {};
        this.clearDirty();
        // The reset is unsaved input too, so a rebuild must not bring the old values back.
        this.ownershipCleared = true;
        this.engineBaseKey = null;
        this.setEngineBase({
          html_engine: inheritedEngines.html_engine || this.engineModels.html_engine,
          screenshot_engine:
            inheritedEngines.screenshot_engine || this.engineModels.screenshot_engine,
        });
        this.ownEngines = [];
        const source = {
          html_config: htmlConfig || {},
          screenshot_config: screenshotConfig || {},
          curl_config: curlConfig || {},
        };

        // Both models are filled, so the reset also holds after a switch of engine mode.
        const forms = {};
        const ownKeys = {};
        const dirtyKeys = { html_config: [], screenshot_config: [], curl_config: [] };
        CONFIG_SECTIONS.forEach((section) => {
          const fields = this.fieldsOf(section.name);
          forms[section.name] = configToForm(source[section.name], fields);
          ownKeys[section.name] = [];
          dirtyKeys[section.name] = fields.map((field) => field.key);
        });
        this.sharedFields.forEach((field) => {
          configsAcceptingKey(field.key).forEach((name) => {
            this.markDirty(dirtyKeys[name], field.key);
          });
        });
        forms.curl_config = configToForm(source.curl_config, this.curlFieldList);
        ownKeys.curl_config = [];
        dirtyKeys.curl_config = this.curlFieldList.map((field) => field.key);
        this.forms = forms;
        this.ownKeys = ownKeys;
        this.dirtyKeys = dirtyKeys;
        this.sharedForm = configToForm(this.flatten(source), this.sharedFields);
        this.sharedOwnKeys = [];
      },
    },
  };
</script>

<style scoped>
  .rendering-layer-form .config-section {
    height: 100%;
  }
</style>
